import axiosInstance from '../helpers/api';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface PropertyResult {
  id: string;
  mlsNumber: string;
  address: string;
  price: number;
  beds: number;
  baths: number;
  sqft: number;
  images?: string[];
  whyItMatches?: string;
  listingBrokerage?: string;
  listingAgent?: string;
}

interface ChatResponse {
  message: string;
  properties?: PropertyResult[];
  intent?: {
    type: string;
    parameters: any;
  };
  suggestedActions?: Array<{
    label: string;
    action: string;
  }>;
  shouldSearchProperties?: boolean;
}

interface ConversationContext {
  hasLocation: boolean;
  hasBudget: boolean;
  hasFeatures: boolean;
  conversationStage: 'greeting' | 'qualifying' | 'searching' | 'viewing';
  extractedCriteria: {
    city?: string;
    state?: string;
    zipCode?: string;
    maxPrice?: number;
    minPrice?: number;
    beds?: number;
    baths?: number;
    propertyType?: string;
    features?: string[];
    purpose?: string;
    yearBuilt?: number;
    minYearBuilt?: number;
    maxYearBuilt?: number;
  };
}

class ChatRealEstateConversationalService {
  private sessionId: string;
  private conversationHistory: ChatMessage[] = [];
  private context: ConversationContext = {
    hasLocation: false,
    hasBudget: false,
    hasFeatures: false,
    conversationStage: 'greeting',
    extractedCriteria: {}
  };

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeConversation();
  }

  private generateSessionId(): string {
    return `chat_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }

  private initializeConversation() {
    // Check if we're on aisearchportal.com
    const isAISearchPortal = window.location.hostname.includes('aisearchportal.com');
    
    // Check for buyer context from portal
    const buyerContextStr = localStorage.getItem('portalBuyerContext');
    let buyerContext: any = null;
    let systemPrompt = `You are a friendly, knowledgeable real estate assistant helping people find their dream homes on chat.realestate.
        You should be warm, conversational, and helpful - like a knowledgeable friend, not a salesperson.
        When someone provides specific details about what they're looking for, acknowledge them and offer to search.
        Don't ask for information they've already provided.`;
    
    if (buyerContextStr && isAISearchPortal) {
      try {
        buyerContext = JSON.parse(buyerContextStr);
        console.log('🏠 [CHAT] AISearchPortal - Initializing with buyer context:', buyerContext);
        
        // Update context with buyer preferences
        if (buyerContext.preferences) {
          const prefs = buyerContext.preferences;
          
          // Update extracted criteria
          if (prefs.budgetMin || prefs.budgetMax) {
            this.context.hasBudget = true;
            this.context.extractedCriteria.minPrice = prefs.budgetMin;
            this.context.extractedCriteria.maxPrice = prefs.budgetMax;
          }
          
          if (prefs.cities && prefs.cities.length > 0) {
            this.context.hasLocation = true;
            this.context.extractedCriteria.city = prefs.cities.join(', ');
          }
          
          if (prefs.bedrooms) {
            this.context.hasFeatures = true;
            this.context.extractedCriteria.beds = prefs.bedrooms;
          }
          
          if (prefs.bathrooms) {
            this.context.extractedCriteria.baths = prefs.bathrooms;
          }
          
          if (prefs.propertyType) {
            this.context.extractedCriteria.propertyType = prefs.propertyType;
          }
          
          // For AISearchPortal, immediately search without conversation
          systemPrompt = `You are an AI real estate search assistant on AISearchPortal.com.
          You have received search criteria from ${buyerContext.name}'s agent.
          
          Search Criteria:
          - Budget: $${prefs.budgetMin?.toLocaleString() || '0'} - $${prefs.budgetMax?.toLocaleString() || 'any'}
          - Looking in: ${prefs.cities?.join(', ') || 'Not specified'}
          - Property type: ${prefs.propertyType || 'Any'}
          - Bedrooms: ${prefs.bedrooms || 'Any'}
          - Bathrooms: ${prefs.bathrooms || 'Any'}
          ${prefs.motivation ? `- Motivation: ${prefs.motivation}` : ''}
          
          When responding, immediately acknowledge the search criteria and indicate you're searching for matching properties.
          Focus on finding properties that match these specific requirements.`;
          
          // Set conversation stage to searching since we have preferences
          this.context.conversationStage = 'searching';
          
          // Mark that we should auto-search on first interaction
          (this as any).shouldAutoSearch = true;
        }
      } catch (error) {
        console.error('Failed to parse buyer context:', error);
      }
    } else if (!isAISearchPortal) {
      console.log('💬 [CHAT] Chat.realestate - Starting conversational flow');
    }
    
    // System prompt for natural conversation
    this.conversationHistory.push({
      role: 'system',
      content: systemPrompt
    });
  }

  /**
   * Main entry point for chat interactions
   */
  public async sendMessage(userMessage: string): Promise<ChatResponse> {
    // Add user message to history
    this.conversationHistory.push({
      role: 'user',
      content: userMessage
    });

    // Extract context from message
    this.updateContext(userMessage);

    // First try to get AI response
    try {
      const aiResponse = await this.getConversationalResponse(userMessage);
      
      // Add AI response to history
      this.conversationHistory.push({
        role: 'assistant',
        content: aiResponse.message
      });

      // If AI suggests searching or user has enough info, search properties
      if (aiResponse.shouldSearchProperties || (this.isReadyForPropertySearch() && this.shouldSearchNow(userMessage))) {
        const properties = await this.searchProperties();
        return {
          ...aiResponse,
          properties,
          shouldSearchProperties: true
        };
      }

      return aiResponse;
    } catch (error) {
      console.error('Error in chat service:', error);
      return {
        message: "I apologize, but I'm having trouble connecting to our services. Please try again in a moment.",
        shouldSearchProperties: false
      };
    }
  }

  /**
   * Get context information for UI display
   */
  public getContext() {
    return {
      sessionId: this.sessionId,
      conversationDepth: this.conversationHistory.length,
      stage: this.context.conversationStage,
      extractedInfo: this.context.extractedCriteria,
      hasComprehensiveInfo: this.isReadyForPropertySearch()
    };
  }

  /**
   * Trigger initial search for AISearchPortal with buyer context
   */
  public async triggerInitialSearch(): Promise<ChatResponse> {
    const isAISearchPortal = window.location.hostname.includes('aisearchportal.com');
    const buyerContextStr = localStorage.getItem('portalBuyerContext');
    
    if (isAISearchPortal && buyerContextStr && (this as any).shouldAutoSearch) {
      try {
        const buyerContext = JSON.parse(buyerContextStr);
        console.log('🔍 [CHAT] AISearchPortal - Triggering auto-search with buyer context');
        
        // Create an automatic initial message
        const autoMessage = `Hi, I'm ${buyerContext.name}. I'm looking for properties based on the preferences my agent shared with you.`;
        
        // Send the message which will trigger search
        return await this.sendMessage(autoMessage);
      } catch (error) {
        console.error('Failed to trigger auto-search:', error);
      }
    }
    
    // Return empty response for non-portal or no context
    return {
      message: '',
      shouldSearchProperties: false
    };
  }

  /**
   * Get conversational response from LLM (Claude/GPT)
   */
  private async getConversationalResponse(userMessage: string): Promise<ChatResponse> {
    try {
      console.log('🤖 [CHAT] Sending to AI with conversation history:', this.conversationHistory.length, 'messages');
      console.log('📝 [CHAT] Context:', this.context.extractedCriteria);
      console.log('🔗 [CHAT] API Endpoint:', '/chat/public/realestate');
      
      // Use the new public chat endpoint that connects to AI/ML service
      const response = await axiosInstance.post('/chat/public/realestate', {
        message: userMessage,
        messages: this.conversationHistory,
        sessionId: this.sessionId,
        conversationHistory: this.conversationHistory,
        context: {
          source: 'chat.realestate',
          stage: this.context.conversationStage,
          extractedInfo: this.context.extractedCriteria,
          conversationDepth: this.conversationHistory.length,
          hasComprehensiveInfo: this.isReadyForPropertySearch()
        }
      });
      
      console.log('✅ [CHAT] Response received:', {
        status: response.status,
        data: response.data
      });

      const aiResponse = response.data.response || response.data.message || "I understand. Let me help you find the perfect property.";
      
      // Check if backend says we should search
      const shouldSearch = response.data.shouldSearchProperties || 
                          response.data.shouldSearch ||
                          (response.data.properties && response.data.properties.length > 0);
      
      // Check if response includes properties
      const hasProperties = response.data.properties && response.data.properties.length > 0;
      
      // Determine if we should search based on AI response
      const shouldSearchProperties = shouldSearch || 
                          hasProperties || 
                          this.shouldSuggestPropertySearch(aiResponse) || 
                          (this.isReadyForPropertySearch() && aiResponse.toLowerCase().includes('search'));
      
      return {
        message: aiResponse,
        shouldSearchProperties: shouldSearchProperties,
        properties: hasProperties ? response.data.properties : undefined,
        intent: response.data.intent
      };
    } catch (error: any) {
      console.error('❌ [CHAT] API Error:', error);
      console.error('❌ [CHAT] Error details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      
      // Provide specific error messages
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        console.error('❌ [CHAT] Backend not reachable - check if server is running on port 8080');
      } else if (error.response?.status === 404) {
        console.error('❌ [CHAT] API endpoint not found - check route configuration');
      } else if (error.response?.status === 500) {
        console.error('❌ [CHAT] Server error - check backend logs');
      }
      
      // Fallback to OpenAI/Anthropic directly if internal endpoint fails
      return this.getFallbackLLMResponse(userMessage);
    }
  }

  /**
   * Fallback to direct LLM API if internal endpoint fails
   */
  private async getFallbackLLMResponse(userMessage: string): Promise<ChatResponse> {
    // Check if user provided comprehensive info upfront
    if (this.isReadyForPropertySearch()) {
      const criteria = this.context.extractedCriteria;
      const features = criteria.features?.join(', ') || '';
      const location = criteria.city || '';
      const beds = criteria.beds || '';
      
      return {
        message: `Perfect! I can see you're looking for ${beds ? beds + '-bedroom' : ''} homes in ${location}${features ? ' with ' + features : ''}. That's very specific - let me search for properties that match all your criteria...`,
        shouldSearchProperties: true
      };
    }

    // Progressive conversation based on stage
    const stage = this.context.conversationStage;
    
    if (stage === 'greeting') {
      return {
        message: "I'd be happy to help you find your perfect home! What brings you to the real estate market today? Are you looking to buy your first home, upgrade to something bigger, or perhaps exploring investment opportunities?",
        shouldSearchProperties: false
      };
    }
    
    if (stage === 'qualifying' && !this.context.hasLocation) {
      return {
        message: "I understand. Can you tell me a bit more about what's motivating your move? Whether it's a job change, growing family, or lifestyle shift, it helps me find homes that truly fit your next chapter.",
        shouldSearchProperties: false
      };
    }
    
    if (stage === 'qualifying' && !this.context.hasBudget) {
      return {
        message: "That's helpful to know! To show you homes that fit comfortably within your plans, what price range were you thinking? I can also explain financing options if that would be helpful.",
        shouldSearchProperties: false
      };
    }
    
    if (this.isReadyForPropertySearch()) {
      return {
        message: "Based on everything you've shared, I have a great understanding of what you're looking for. Let me search for some homes that match your needs...",
        shouldSearchProperties: true
      };
    }
    
    return {
      message: "Tell me more about what your ideal home looks like. What features are must-haves versus nice-to-haves?",
      shouldSearchProperties: false
    };
  }

  /**
   * Determine if AI response suggests we should search for properties
   */
  private shouldSuggestPropertySearch(aiResponse: string): boolean {
    const searchPhrases = [
      'let me search',
      'let me find',
      'let me look for',
      'searching for properties',
      'based on your criteria',
      'match your needs',
      'here are some properties',
      'found some options'
    ];
    
    const lowercaseResponse = aiResponse.toLowerCase();
    return searchPhrases.some(phrase => lowercaseResponse.includes(phrase));
  }

  /**
   * Update conversation context from user message
   */
  private updateContext(message: string) {
    const lowerMessage = message.toLowerCase();
    
    // Extract location - All Phoenix metro areas
    const phoenixMetroCities = [
      'gilbert', 'scottsdale', 'phoenix', 'chandler', 'tempe', 'mesa',
      'glendale', 'peoria', 'surprise', 'queen creek', 'paradise valley',
      'cave creek', 'fountain hills', 'avondale', 'goodyear', 'buckeye',
      'gibert' // Common misspelling of Gilbert
    ];
    
    // Create regex that matches any of the cities
    const cityRegex = new RegExp(`(${phoenixMetroCities.join('|')})`, 'i');
    const cityMatch = message.match(cityRegex);
    if (cityMatch) {
      // Capitalize first letter of each word and fix common misspellings
      let cityName = cityMatch[1].split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
      
      // Fix common misspellings
      if (cityName.toLowerCase() === 'gibert') {
        cityName = 'Gilbert';
      }
      
      this.context.extractedCriteria.city = cityName;
      this.context.extractedCriteria.state = 'Arizona';
      this.context.hasLocation = true;
      if (this.context.conversationStage === 'greeting') {
        this.context.conversationStage = 'qualifying';
      }
    }

    // Extract price
    const priceRegex = /(\$?[\d,]+\.?\d*\s*(?:k|mil|million)?)/i;
    const priceMatch = message.match(priceRegex);
    if (priceMatch) {
      const priceStr = priceMatch[1].replace(/[$,]/g, '');
      let price = parseFloat(priceStr);
      
      if (priceStr.toLowerCase().includes('k')) {
        price *= 1000;
      } else if (priceStr.toLowerCase().includes('mil') || priceStr.toLowerCase().includes('million')) {
        price *= 1000000;
      }
      
      // Assume it's max price unless context suggests otherwise
      if (message.toLowerCase().includes('under') || message.toLowerCase().includes('below') || message.toLowerCase().includes('max')) {
        this.context.extractedCriteria.maxPrice = price;
      } else {
        this.context.extractedCriteria.minPrice = price;
        this.context.extractedCriteria.maxPrice = price * 1.2; // Add 20% range
      }
      this.context.hasBudget = true;
    }

    // Extract bedrooms
    const bedroomRegex = /(\d+)\s*(?:bed|bedroom|br)/i;
    const bedroomMatch = message.match(bedroomRegex);
    if (bedroomMatch) {
      this.context.extractedCriteria.beds = parseInt(bedroomMatch[1]);
      this.context.hasFeatures = true;
    }

    // Extract bathrooms
    const bathroomRegex = /(\d+)\s*(?:bath|bathroom|ba)/i;
    const bathroomMatch = message.match(bathroomRegex);
    if (bathroomMatch) {
      this.context.extractedCriteria.baths = parseInt(bathroomMatch[1]);
      this.context.hasFeatures = true;
    }

    // Extract year built
    const yearBuiltRegex = /built\s*(?:no\s*later\s*than|before|after|in)?\s*(\d{4})/i;
    const yearBuiltMatch = message.match(yearBuiltRegex);
    if (yearBuiltMatch) {
      const year = parseInt(yearBuiltMatch[1]);
      if (lowerMessage.includes('no later than') || lowerMessage.includes('before')) {
        this.context.extractedCriteria.maxYearBuilt = year;
      } else if (lowerMessage.includes('after')) {
        this.context.extractedCriteria.minYearBuilt = year;
      } else {
        this.context.extractedCriteria.yearBuilt = year;
      }
      this.context.hasFeatures = true;
    }
    
    // Extract features
    const features = [];
    if (lowerMessage.includes('pool')) features.push('pool');
    if (lowerMessage.includes('garage')) features.push('garage');
    if (lowerMessage.includes('yard')) features.push('yard');
    if (lowerMessage.includes('modern')) features.push('modern');
    if (lowerMessage.includes('single story')) features.push('single story');
    
    if (features.length > 0) {
      this.context.extractedCriteria.features = features;
      this.context.hasFeatures = true;
    }

    // Extract purpose
    if (lowerMessage.includes('upgrade') || lowerMessage.includes('bigger')) {
      this.context.extractedCriteria.purpose = 'upgrade';
    } else if (lowerMessage.includes('downsize')) {
      this.context.extractedCriteria.purpose = 'downsize';
    } else if (lowerMessage.includes('investment')) {
      this.context.extractedCriteria.purpose = 'investment';
    }

    // Update conversation stage
    if (this.isReadyForPropertySearch() && this.context.conversationStage === 'qualifying') {
      this.context.conversationStage = 'searching';
    }
  }

  /**
   * Check if we have enough info to search
   */
  private isReadyForPropertySearch(): boolean {
    return this.context.hasLocation && (
      this.context.hasBudget || 
      this.context.hasFeatures ||
      Object.keys(this.context.extractedCriteria).length >= 2
    );
  }

  /**
   * Check if user is asking to search now
   */
  private shouldSearchNow(message: string): boolean {
    const searchTriggers = [
      'show me',
      'find me',
      'search for',
      'looking for',
      'can you find',
      'what homes',
      'what properties',
      'available homes'
    ];
    
    const lowerMessage = message.toLowerCase();
    return searchTriggers.some(trigger => lowerMessage.includes(trigger));
  }

  /**
   * Search properties using Theta AI
   */
  private async searchProperties(): Promise<PropertyResult[]> {
    try {
      const searchQuery = this.buildSearchQuery();
      console.log('Searching properties with:', searchQuery);
      
      // Use Theta AI for property search with proper error handling
      const response = await axiosInstance.post('/theta-ai/search', {
        query: searchQuery,
        sessionId: this.sessionId,
        userId: 'public-user'
      }).catch(error => {
        console.error('❌ [CHAT] Theta AI search failed:', error);
        // Return empty results on error
        return { data: { properties: [] } };
      });
      
      // Transform Theta AI results to our format
      if (response.data.properties && Array.isArray(response.data.properties)) {
        return this.formatProperties(response.data.properties);
      }
      
      return [];
    } catch (error) {
      console.error('Property search error:', error);
      return [];
    }
  }

  /**
   * Build natural language search query for Theta AI
   */
  private buildSearchQuery(): string {
    const criteria = this.context.extractedCriteria;
    const parts = [];
    
    if (criteria.beds) {
      parts.push(`${criteria.beds} bedroom`);
    }
    
    if (criteria.baths) {
      parts.push(`${criteria.baths} bathroom`);
    }
    
    if (criteria.propertyType) {
      parts.push(criteria.propertyType);
    } else {
      parts.push('homes');
    }
    
    if (criteria.city) {
      parts.push(`in ${criteria.city}`);
    }
    
    if (criteria.maxPrice) {
      parts.push(`under $${criteria.maxPrice.toLocaleString()}`);
    }
    
    if (criteria.features && criteria.features.length > 0) {
      parts.push(`with ${criteria.features.join(', ')}`);
    }
    
    if (criteria.maxYearBuilt) {
      parts.push(`built no later than ${criteria.maxYearBuilt}`);
    } else if (criteria.minYearBuilt) {
      parts.push(`built after ${criteria.minYearBuilt}`);
    } else if (criteria.yearBuilt) {
      parts.push(`built in ${criteria.yearBuilt}`);
    }
    
    return parts.join(' ');
  }

  /**
   * Format Theta AI properties to our interface
   */
  private formatProperties(thetaProperties: any[]): PropertyResult[] {
    return thetaProperties.slice(0, 10).map((prop, index) => {
      // Handle different property formats from backend
      const address = prop.address?.street || 
                     prop.UnparsedAddress || 
                     prop.StreetAddress || 
                     'Address not available';
      
      // Log to debug image issue
      console.log('🏠 [CHAT] Formatting property:', {
        id: prop.id,
        hasImages: !!prop.images,
        imageCount: prop.images?.length || 0,
        firstImage: prop.images?.[0]
      });
      
      return {
        id: prop.id || prop.ListingId || prop.mlsNumber || `prop-${index}`,
        mlsNumber: prop.mlsNumber || prop.ListingId || prop.id || `MLS-${index}`,
        address: address,
        city: prop.city || prop.City || prop.address?.city || '',
        state: prop.state || prop.StateOrProvince || prop.address?.state || 'AZ',
        zipCode: prop.zipCode || prop.PostalCode || prop.address?.zipCode || '',
        price: prop.price || prop.ListPrice || prop.CurrentPrice || 0,
        beds: prop.bedrooms || prop.BedroomsTotal || prop.beds || 0,
        baths: prop.bathrooms || prop.BathroomsFull || prop.baths || 0,
        sqft: prop.sqft || prop.squareFeet || prop.LivingArea || prop.BuildingAreaTotal || 0,
        images: prop.images || prop.Media?.map((m: any) => m.MediaURL) || [],
        latitude: prop.latitude || prop.Latitude || prop.address?.latitude,
        longitude: prop.longitude || prop.Longitude || prop.address?.longitude,
        whyItMatches: this.generateMatchReason(prop),
        listingBrokerage: prop.listingBrokerage || prop.listingOffice || prop.ListOfficeName || prop.ListingOfficeName || prop.OfficeName,
        listingAgent: prop.listingAgent || prop.ListAgentFullName || prop.ListingAgentName || prop.AgentName
      };
    });
  }

  /**
   * Generate a reason why this property matches
   */
  private generateMatchReason(property: any): string {
    const reasons = [];
    const criteria = this.context.extractedCriteria;
    
    if (criteria.beds && property.BedroomsTotal >= criteria.beds) {
      reasons.push(`${property.BedroomsTotal} bedrooms`);
    }
    
    if (criteria.maxPrice && property.ListPrice <= criteria.maxPrice) {
      reasons.push('within budget');
    }
    
    if (criteria.features?.includes('pool') && property.PoolFeatures) {
      reasons.push('has pool');
    }
    
    if (criteria.city && property.City?.toLowerCase() === criteria.city.toLowerCase()) {
      reasons.push(`in ${criteria.city}`);
    }
    
    return reasons.join(', ') || 'Matches your search criteria';
  }

  /**
   * Get conversation insights for UI display
   */
  public getConversationInsights(): any {
    return {
      stage: this.context.conversationStage,
      hasLocation: this.context.hasLocation,
      hasBudget: this.context.hasBudget,
      hasFeatures: this.context.hasFeatures,
      extractedCriteria: this.context.extractedCriteria,
      messageCount: this.conversationHistory.length
    };
  }
}

export default ChatRealEstateConversationalService;